<!-- TEMPLATE CONTRACT: Ui Stock Card List Component owns the visible Angular template structure for src/app/shared/ui/ui-stock-card-list/ui-stock-card-list.component.html. -->
<!-- Conditional branch documents the visible UI state for this condition. -->
@if (items().length === 0) {
  <article class="ui-stock-card-list__empty">
    <p>{{ emptyMessage() | uiLabelTranslate }}</p>
  </article>
} @else {
  <section class="ui-stock-card-list">
    <!-- Repeated block documents how collection data renders in the UI. -->
    @for (category of items(); track category.id) {
      <ui-card>
        <header class="ui-stock-card-list__card-header">
          <div class="ui-stock-card-list__category-heading">
            <span class="ui-stock-card-list__level-label">{{ 'shared.stockCardList.category' | uiLabelTranslate }}</span>
            <h2 class="ui-stock-card-list__category-title">{{ category.name }}</h2>
            <div class="ui-stock-card-list__summary">
              <span>{{ 'shared.stockCardList.productsCount' | uiLabelTranslate }}: {{ categoryProductCount(category) }}</span>
              <span>{{ 'shared.stockCardList.variantsCount' | uiLabelTranslate }}: {{ categoryVariantCount(category) }}</span>
              <span>{{ 'shared.stockCardList.totalStock' | uiLabelTranslate }}: {{ categoryTotalQuantity(category) }}</span>
            </div>
          </div>
          <div class="ui-stock-card-list__card-actions">
            <ui-button
              variant="ghost"
              size="sm"
              [icon]="isExpanded(category.id) ? 'expand_less' : 'expand_more'"
              [ariaLabel]="isExpanded(category.id) ? 'shared.stockCardList.actions.collapseCategory' : 'shared.stockCardList.actions.expandCategory'"
              (click)="toggleCategory(category.id)"
            ></ui-button>
            <!-- Conditional branch documents the visible UI state for this condition. -->
            @if (showCategoryEdit()) {
              <div class="ui-stock-card-list__action ui-stock-card-list__action--edit">
                <ui-button variant="ghost" size="sm" icon="edit" ariaLabel="shared.stockCardList.actions.editCategory" (click)="requestCategoryEdit(category)"></ui-button>
              </div>
            }
            <!-- Conditional branch documents the visible UI state for this condition. -->
            @if (showCategoryRemove()) {
              <div class="ui-stock-card-list__action ui-stock-card-list__action--delete">
                <ui-button variant="ghost" size="sm" icon="delete" ariaLabel="shared.stockCardList.actions.removeCategory" (click)="requestCategoryRemove(category)"></ui-button>
              </div>
            }
          </div>
        </header>
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (isExpanded(category.id)) {
          <div class="ui-stock-card-list__card-body">
            <div class="ui-stock-card-list__carousel-header">
              <span class="ui-stock-card-list__level-label">{{ 'shared.stockCardList.productsAndVariants' | uiLabelTranslate }}</span>
              <div class="ui-stock-card-list__carousel-actions">
                <ui-button variant="ghost" size="sm" icon="chevron_left" ariaLabel="shared.stockCardList.actions.previousProducts" (click)="scrollProducts(category.id, 'previous')"></ui-button>
                <ui-button variant="ghost" size="sm" icon="chevron_right" ariaLabel="shared.stockCardList.actions.nextProducts" (click)="scrollProducts(category.id, 'next')"></ui-button>
              </div>
            </div>

            <div #productRail class="ui-stock-card-list__product-rail" [attr.data-category-id]="category.id">
              <!-- Repeated block documents how collection data renders in the UI. -->
              @for (product of category.products; track product.id) {
                <!-- Conditional branch documents the visible UI state for this condition. -->
                @if (product.isCashProduct) {
                  <section class="ui-stock-card-list__product">
                    <header class="ui-stock-card-list__product-header">
                      <span class="ui-stock-card-list__level-label">{{ 'shared.stockCardList.product' | uiLabelTranslate }}</span>
                      <p class="ui-stock-card-list__product-title">{{ product.name }}</p>
                    </header>
                    <article class="ui-stock-card-list__variant-row">
                      <div class="ui-stock-card-list__variant-copy">
                        <span class="ui-stock-card-list__level-label ui-stock-card-list__level-label--variant">{{ 'shared.stockCardList.quantity' | uiLabelTranslate }}</span>
                      </div>
                      <div class="ui-stock-card-list__variant-qty-wrap">
                        <span class="ui-stock-card-list__variant-qty">{{ product.totalQuantity ?? 0 }}</span>
                      </div>
                    </article>
                  </section>
                } @else {
                  <section class="ui-stock-card-list__product">
                    <header class="ui-stock-card-list__product-header">
                      <span class="ui-stock-card-list__level-label">{{ 'shared.stockCardList.product' | uiLabelTranslate }}</span>
                      <p class="ui-stock-card-list__product-title">{{ product.name }}</p>
                      <!-- Conditional branch documents the visible UI state for this condition. -->
                      @if (showProductStatus() && product.status && product.statusLabel) {
                      <div class="ui-stock-card-list__product-summary">
                        <ui-badge [label]="product.statusLabel" [tone]="badgeTone(product.status)"></ui-badge>
                        <div class="ui-stock-card-list__product-metrics">
                          <span>{{ product.totalQuantity ?? 0 }} {{ 'uiLabels.total' | uiLabelTranslate }}</span>
                          <span>{{ 'stockThresholds.table.threshold' | uiLabelTranslate }} {{ product.threshold ?? 0 }}</span>
                        </div>
                      </div>
                      }
                    </header>

                    <div class="ui-stock-card-list__variant-list">
                      <!-- Repeated block documents how collection data renders in the UI. -->
                      @for (variant of product.variants; track variant.id) {
                        <article class="ui-stock-card-list__variant-row" [class.ui-stock-card-list__variant-row--with-status]="showVariantStatus() && !!variant.status && !!variant.statusLabel">
                          <div class="ui-stock-card-list__variant-copy">
                            <span class="ui-stock-card-list__level-label ui-stock-card-list__level-label--variant">{{ 'shared.stockCardList.variant' | uiLabelTranslate }}</span>
                            <p class="ui-stock-card-list__variant-name">{{ variant.name }}</p>
                          </div>
                          <!-- Conditional branch documents the visible UI state for this condition. -->
                          @if (showVariantStatus() && variant.status && variant.statusLabel) {
                            <div class="ui-stock-card-list__variant-status">
                              <ui-badge [label]="variant.statusLabel" [tone]="badgeTone(variant.status)"></ui-badge>
                            </div>
                          }
                          <div class="ui-stock-card-list__variant-qty-wrap">
                            <span class="ui-stock-card-list__variant-qty">{{ variant.quantity }}</span>
                          </div>
                        </article>
                      }
                    </div>
                  </section>
                }
              }
            </div>
          </div>
        }
      </ui-card>
    }
  </section>
}
